import * as Haptics from "expo-haptics";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import DraggableFlatList, {
  type RenderItemParams,
} from "react-native-draggable-flatlist";

import type { BusinessItem } from "../types";
import { normalizeOrder, sortByOrder } from "../utils";

type Props = {
  items: BusinessItem[];
  onReorder: (next: BusinessItem[]) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  contentBottomPad?: number;
  style?: StyleProp<ViewStyle>;

  /**
   * Mobile can place the page title/instructions inside the FlatList so the
   * whole screen scrolls as one surface.
   */
  ListHeaderIntroComponent?: ReactNode;
  useScrollViewOnWeb?: boolean;

  /**
   * Desktop/web may still need the built-in list header.
   * Mobile can also use this header so the Add/Edit form can sit below the list.
   */
  showHeader?: boolean;

  /**
   * Useful on mobile where the drag hint should only show when there are
   * at least 2 items.
   */
  showDragHint?: boolean;

  /**
   * Used by mobile layout to place EmptyItemsState inside the scrollable list.
   */
  ListEmptyComponent?: ReactNode;

  /**
   * Used by mobile layout to place the Add/Edit form below the items list.
   */
  ListFooterComponent?: ReactNode;

  /**
   * When this value changes, the list scrolls to the footer. This helps when
   * the user taps Edit and the edit form is below the items.
   */
  scrollToFooterKey?: string | number | null;
};

export default function ItemsListDraggable({
  items,
  onReorder,
  onDelete,
  onEdit,
  contentBottomPad = 16,
  style,
  ListHeaderIntroComponent,
  useScrollViewOnWeb = false,
  showHeader = true,
  showDragHint,
  ListEmptyComponent,
  ListFooterComponent,
  scrollToFooterKey = null,
}: Props) {
  const listRef = useRef<any>(null);

  const ordered = useMemo(() => sortByOrder(items), [items]);
  const [data, setData] = useState<BusinessItem[]>(ordered);

  useEffect(() => {
    setData((prev) => {
      const byId = new Map(ordered.map((item) => [item.id, item] as const));

      const merged = prev
        .map((item) => byId.get(item.id) ?? item)
        .filter((item) => byId.has(item.id));

      const existingIds = new Set(merged.map((item) => item.id));
      const additions = ordered.filter((item) => !existingIds.has(item.id));

      return [...merged, ...additions];
    });
  }, [ordered]);

  useEffect(() => {
    if (!scrollToFooterKey) return;

    const timeout = setTimeout(() => {
      listRef.current?.scrollToEnd?.({
        animated: true,
      });
    }, 120);

    return () => clearTimeout(timeout);
  }, [scrollToFooterKey]);

  const isWebScrollFallback = Platform.OS === "web" && useScrollViewOnWeb;
  const shouldShowDragHint =
    !isWebScrollFallback &&
    (typeof showDragHint === "boolean" ? showDragHint : ordered.length > 0);

  const renderItem = useCallback(
    ({ item, drag, isActive }: RenderItemParams<BusinessItem>) => {
      return (
        <Pressable
          onLongPress={async () => {
            if (Platform.OS !== "web") {
              await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
            }

            drag();
          }}
          delayLongPress={180}
          disabled={isActive}
          style={{
            padding: 12,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#E5E7EB",
            backgroundColor: isActive ? "#F3F4F6" : "#FFFFFF",
            marginBottom: 10,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          {item.imageUri ? (
            <Image
              source={{ uri: item.imageUri }}
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                backgroundColor: "rgba(0,0,0,0.06)",
              }}
              resizeMode={item.imageFit === "contain" ? "contain" : "cover"}
            />
          ) : (
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                backgroundColor: "rgba(0,0,0,0.06)",
              }}
            />
          )}

          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 15,
                fontWeight: "700",
                color: "#111827",
              }}
              numberOfLines={1}
            >
              {item.name}
            </Text>
          </View>

          <Pressable
            onPress={() => onEdit(item.id)}
            hitSlop={8}
            style={{ paddingVertical: 6, paddingHorizontal: 8 }}
          >
            <Text style={{ fontWeight: "700", color: "#111827" }}>Edit</Text>
          </Pressable>

          <Pressable
            onPress={() => onDelete(item.id)}
            hitSlop={8}
            style={{ paddingVertical: 6, paddingHorizontal: 8 }}
          >
            <Text style={{ fontWeight: "700", color: "#B91C1C" }}>
              Delete
            </Text>
          </Pressable>

          <Text style={{ fontSize: 18, opacity: 0.6 }}>☰</Text>
        </Pressable>
      );
    },
    [onDelete, onEdit]
  );

  const moveItem = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (toIndex < 0 || toIndex >= data.length || fromIndex === toIndex) {
        return;
      }

      const next = [...data];
      const [moved] = next.splice(fromIndex, 1);

      if (!moved) return;

      next.splice(toIndex, 0, moved);

      const normalized = normalizeOrder(next);

      setData(normalized);
      onReorder(normalized);
    },
    [data, onReorder]
  );

  const renderWebScrollItem = useCallback(
    (item: BusinessItem, index: number, totalItems: number) => {
      const canMoveUp = index > 0;
      const canMoveDown = index < totalItems - 1;

      return (
        <View
          style={{
            padding: 12,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#E5E7EB",
            backgroundColor: "#FFFFFF",
            marginBottom: 10,
            gap: 10,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
            }}
          >
            {item.imageUri ? (
              <Image
                source={{ uri: item.imageUri }}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: "rgba(0,0,0,0.06)",
                }}
                resizeMode={item.imageFit === "contain" ? "contain" : "cover"}
              />
            ) : (
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: "rgba(0,0,0,0.06)",
                }}
              />
            )}

            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: "700",
                  color: "#111827",
                }}
                numberOfLines={1}
              >
                {item.name}
              </Text>
            </View>
          </View>

          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            {canMoveUp ? (
              <Pressable
                onPress={() => moveItem(index, index - 1)}
                style={{
                  minHeight: 44,
                  paddingHorizontal: 12,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: "#D1D5DB",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text style={{ fontWeight: "800", color: "#111827" }}>
                  Move up
                </Text>
              </Pressable>
            ) : null}

            {canMoveDown ? (
              <Pressable
                onPress={() => moveItem(index, index + 1)}
                style={{
                  minHeight: 44,
                  paddingHorizontal: 12,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: "#D1D5DB",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text style={{ fontWeight: "800", color: "#111827" }}>
                  Move down
                </Text>
              </Pressable>
            ) : null}

            <Pressable
              onPress={() => onEdit(item.id)}
              style={{
                minHeight: 44,
                paddingHorizontal: 12,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text style={{ fontWeight: "800", color: "#111827" }}>Edit</Text>
            </Pressable>

            <Pressable
              onPress={() => onDelete(item.id)}
              style={{
                minHeight: 44,
                paddingHorizontal: 12,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text style={{ fontWeight: "800", color: "#B91C1C" }}>
                Delete
              </Text>
            </Pressable>
          </View>
        </View>
      );
    },
    [moveItem, onDelete, onEdit]
  );

  const listHeader = useMemo(() => {
    if (!ListHeaderIntroComponent && !showHeader) return null;

    return (
      <>
        {ListHeaderIntroComponent ? <>{ListHeaderIntroComponent}</> : null}

        {showHeader ? (
          <View style={{ marginBottom: 8 }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: "800",
                  color: "#111827",
                }}
              >
                Items ({ordered.length})
              </Text>

              {shouldShowDragHint ? (
                <Text
                  style={{
                    fontSize: 11,
                    color: "#6B7280",
                    fontWeight: "600",
                  }}
                >
                  Drag to reorder
                </Text>
              ) : null}
            </View>
          </View>
        ) : null}
      </>
    );
  }, [
    ListHeaderIntroComponent,
    ordered.length,
    shouldShowDragHint,
    showHeader,
  ]);

  const listFooter = ListFooterComponent ? <>{ListFooterComponent}</> : null;

  if (isWebScrollFallback) {
    return (
      <ScrollView
        ref={listRef}
        style={style}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: contentBottomPad,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {listHeader}

        {data.length === 0
          ? ListEmptyComponent
          : data.map((item, index) => (
              <React.Fragment key={item.id}>
                {renderWebScrollItem(item, index, data.length)}
              </React.Fragment>
            ))}

        {listFooter}
      </ScrollView>
    );
  }

  return (
    <DraggableFlatList
      ref={listRef}
      containerStyle={style}
      style={style}
      data={data}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      activationDistance={10}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "on-drag"}
      onDragEnd={({ data: next }) => {
        const normalized = normalizeOrder(next);

        setData(normalized);
        onReorder(normalized);
      }}
      ListHeaderComponent={listHeader}
      ListEmptyComponent={ListEmptyComponent ? <>{ListEmptyComponent}</> : null}
      ListFooterComponent={listFooter}
      contentContainerStyle={{
        flexGrow: 1,
        paddingBottom: contentBottomPad,
      }}
      showsVerticalScrollIndicator={false}
    />
  );
}
